import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  Button,
  Divider,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  Box,
  FormLabel,
  MenuItem,
  Select,
  CircularProgress,
} from "@mui/material";
import AuthWrapper1 from "../AuthWrapper1";
import AuthCardWrapper from "../AuthCardWrapper";
import Logo from "ui-component/Logo";
import AuthFooter from "ui-component/cards/AuthFooter";
import MedTecLogo from "../../../../assets/images/MEDTEC_LOGO.png";
import useAuth from "hooks/useAuth";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { SNACKBAR_OPEN } from "store/actions";
import { OtpInput } from "ui-component/otp-input/OtpInput";
import { enableOtp, loginAPI, getVerificationCode } from "apis/api";

const OtpConfirmation = () => {
  const theme = useTheme();
  const { cellPhone, registryNo, pwd, authorize } = useAuth();
  const navigate = useNavigate();
  const matchDownSM = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
    const [initializing, setInitializing] = useState(true);

  const [time, setTime] = useState(90);
  const [code, setCode] = useState("");
  const [disableResend, setDisableResend] = useState(true);
  const [selectedPhone, setSelectedPhone] = useState("");
  const [isOtpStep, setIsOtpStep] = useState(false);
  const [loading, setLoading] = useState(false);

  const phoneList = useMemo(
    () => (Array.isArray(cellPhone) ? cellPhone : [cellPhone]).filter((p) => p && p.trim() !== ""),
    [cellPhone]
  );

  const sentRef = useRef(false);

    useEffect(() => {
        const init = async () => {
            if (!cellPhone || phoneList.length === 0) {
                navigate("/login");
                return;
            }

            if (phoneList.length === 1) {
                sentRef.current = true;
                setSelectedPhone(phoneList[0]);

                try {
                    setLoading(true);
                    const res = await getVerificationCode({
                        registryNo,
                        cellPhone: phoneList[0],
                    });

                    if (res?.data?.success) {
                        dispatch({
                            type: SNACKBAR_OPEN,
                            open: true,
                            message: "Doğrulama kodu gönderildi",
                            variant: "alert",
                            alertSeverity: "success",
                        });
                        setIsOtpStep(true);
                    } else {
                        dispatch({
                            type: SNACKBAR_OPEN,
                            open: true,
                            message: "Kod gönderilemedi",
                            variant: "alert",
                            alertSeverity: "error",
                        });
                    }
                } catch (err) {
                    console.error(err);
                } finally {
                    setLoading(false);
                    setInitializing(false);
                }
                return;
            }

            setInitializing(false);
        };

        init();
    }, [phoneList, registryNo, dispatch, navigate]);

  useEffect(() => {
    const sendCodeIfSinglePhone = async () => {
      if (sentRef.current) return;
      if (phoneList.length === 1) {
        sentRef.current = true;
        try {
          setSelectedPhone(phoneList[0]);
          setLoading(true);
          const res = await getVerificationCode({
            registryNo,
            cellPhone: phoneList[0],
          });
          if (res?.data?.success) {
            dispatch({
              type: SNACKBAR_OPEN,
              open: true,
              message: "Doğrulama kodu gönderildi",
              variant: "alert",
              alertSeverity: "success",
            });
            setIsOtpStep(true);
          } else {
            dispatch({
              type: SNACKBAR_OPEN,
              open: true,
              message: "Kod gönderilemedi",
              variant: "alert",
              alertSeverity: "error",
            });
          }
        } catch (err) {
          console.error(err);
          dispatch({
            type: SNACKBAR_OPEN,
            open: true,
            message: "Kod gönderilirken bir hata oluştu",
            variant: "alert",
            alertSeverity: "error",
          });
        } finally {
          setLoading(false);
        }
      }
    };
    sendCodeIfSinglePhone();
  }, [phoneList, registryNo, dispatch]);

  const handleResend = async () => {
    try {
      if (disableResend || !selectedPhone) return;

      await getVerificationCode({
        registryNo,
        cellPhone: selectedPhone,
      });

      dispatch({
        type: SNACKBAR_OPEN,
        open: true,
        message: "Kod yeniden gönderildi",
        variant: "alert",
        alertSeverity: "success",
      });

      setDisableResend(true);
      setTime(90);
    } catch (err) {
      console.error(err);
      dispatch({
        type: SNACKBAR_OPEN,
        open: true,
        message: "İşlem sırasında bir hata oluştu",
        variant: "alert",
        alertSeverity: "error",
      });
    }
  };

  const handleSendCode = async () => {
    if (!selectedPhone) return;
    try {
      setLoading(true);
      const res = await getVerificationCode({
        registryNo,
        cellPhone: selectedPhone,
      });
      if (res?.data?.success) {
        dispatch({
          type: SNACKBAR_OPEN,
          open: true,
          message: "Doğrulama kodu gönderildi",
          variant: "alert",
          alertSeverity: "success",
        });
        setIsOtpStep(true);
      } else {
        dispatch({
          type: SNACKBAR_OPEN,
          open: true,
          message: "Kod gönderilemedi",
          variant: "alert",
          alertSeverity: "error",
        });
      }
    } catch (err) {
      console.error(err);
      dispatch({
        type: SNACKBAR_OPEN,
        open: true,
        message: "Kod gönderilirken bir hata oluştu",
        variant: "alert",
        alertSeverity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (authenticatorCode) => {
    try {
      const res = await loginAPI({
        registryNo,
        password: pwd,
        authenticatorCode,
      });
      if (res?.status === 400) {
        dispatch({
          type: SNACKBAR_OPEN,
          open: true,
          message: res?.data ?? "İşlem sırasında bir hata oluştu",
          variant: "alert",
          alertSeverity: "error",
        });
      } else {
        dispatch({
          type: SNACKBAR_OPEN,
          open: true,
          message: "Kod başarıyla doğrulandı",
          variant: "alert",
          alertSeverity: "success",
        });
        authorize(res?.data?.data?.token);
      }
    } catch (err) {
      console.error(err);
      dispatch({
        type: SNACKBAR_OPEN,
        open: true,
        message: "İşlem sırasında bir hata oluştu",
        variant: "alert",
        alertSeverity: "error",
      });
    }
  };
  if (initializing) return null;
  return (
    <AuthWrapper1>
      <Grid container direction="column" justifyContent="flex-end" sx={{ minHeight: "100vh" }}>
        <Grid item xs={12}>
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            sx={{ minHeight: "calc(100vh - 68px)" }}
          >
            <Grid item sx={{ m: { xs: 1, sm: 3 }, mb: 0 }}>
              <AuthCardWrapper>
                <Grid container spacing={2} alignItems="center" justifyContent="center">
                  <Grid item>
                    <Link to="#">
                      <Logo />
                    </Link>
                  </Grid>

                  <Grid item xs={12}>
                    <Stack alignItems="center" spacing={1}>
                      <Typography
                        color={theme.palette.secondary.main}
                        gutterBottom
                        variant={matchDownSM ? "h3" : "h2"}
                      >
                        Hoş Geldiniz
                      </Typography>
                      <Typography variant="caption" textAlign="center" fontSize="16px">
                        {isOtpStep
                          ? "Telefonunuza gönderilen güvenlik kodunu giriniz"
                          : "Lütfen doğrulama için bir telefon numarası seçiniz"}
                      </Typography>
                    </Stack>
                  </Grid>

                  {!isOtpStep && (
                    <Grid item xs={12}>
                      <Select
                        value={selectedPhone}
                        onChange={(e) => setSelectedPhone(e.target.value)}
                        fullWidth
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          Telefon Seçiniz
                        </MenuItem>
                        {phoneList.map((phone) => (
                          <MenuItem key={phone} value={phone}>
                            {`+90 *** *** ${phone.slice(-4)}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>
                  )}

                  {!isOtpStep && (
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={!selectedPhone || loading}
                        onClick={handleSendCode}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                      >
                        {loading ? "Gönderiliyor..." : "Kodu Gönder"}
                      </Button>
                    </Grid>
                  )}

                  {isOtpStep && (
                    <>
                      <Grid display="flex" item justifyContent="center" xs={12}>
                        <Typography variant="h3">{`+90 *** *** ${selectedPhone.slice(
                          -4
                        )}`}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <OtpInput
                          length={6}
                          onChange={setCode}
                          onComplete={handleSubmit}
                          onTimeExpire={() => {
                            setTime(null);
                            setDisableResend(false);
                          }}
                          piece={2}
                          time={time}
                          value={code}
                        />
                      </Grid>

                      {!disableResend && (
                        <Grid display="flex" item justifyContent="center" xs={12}>
                          <Typography
                            component="span"
                            onClick={handleResend}
                            sx={{
                              cursor: disableResend ? "inherit" : "pointer",
                              color: (theme) =>
                                disableResend
                                  ? theme.palette.action.disabled
                                  : theme.palette.secondary.main,
                              textDecoration: "underline",
                              "&:hover": disableResend ? {} : { opacity: 0.7 },
                            }}
                            variant="subtitle2"
                          >
                            {"Kodu yeniden gönder"}
                          </Typography>
                        </Grid>
                      )}
                    </>
                  )}

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid xs={12} mt={2}>
                    <Box textAlign="center">
                      <img alt="medtecLogo" src={MedTecLogo} width={130} /> <br />
                      <FormLabel>
                        © {new Date().getFullYear()} MEDTEC Bilgi Teknolojileri ve Yazılım A.Ş. -
                        Tüm Hakları Saklıdır
                      </FormLabel>
                    </Box>
                  </Grid>
                </Grid>
              </AuthCardWrapper>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ m: 3, mt: 1 }}>
          <AuthFooter />
        </Grid>
      </Grid>
    </AuthWrapper1>
  );
};

export default OtpConfirmation;
